/**
 * "Leave site?" before OPButtons closes or reloads mid-show (2026-11-03, operator: "it should always show if a
 * timeline is playing via opbuttons regardless of performance mode. and always in performance mode").
 *
 * Closing the tab, Ctrl+W, F5 or the browser's reload went straight through. The show itself keeps running (OPBridge
 * holds performance mode and plays the timelines), but the controls are gone until the page is opened again. So the
 * browser's own "Leave site?" asks first while performance mode is on, or while a timeline is queued or playing on any
 * computer OPButtons follows (the count-in follows the primary and every paired peer for the whole session, so this
 * is always known). A paused timeline does not ask.
 *
 * OPButtons' own reloads (applying an update) say so first (`leaveOnPurpose`) and are not asked about. The browser
 * shows the prompt only after a press on the page (Chrome's rule), and the iPad's home-screen app never shows it:
 * there it is the desktop's guard.
 */

import { performanceMode } from '../opbridge/performanceMode.ts';
import { anyTimelineRunning } from '../timeline/transport.ts';

let onPurpose = false;
/** Harness kill switch (the bot sets it unless a smoke opts in, `browserGuards`): Playwright answers an unexpected
 *  "Leave site?" with Stay, and the many smokes that reload mid-run would never reload. */
const DISABLE_KEY = 'opbuttons.leaveGuard.disabled';
const disabled = (): boolean => { try { return localStorage.getItem(DISABLE_KEY) === '1'; } catch { return false; } };

/** Why leaving now should ask, or null. */
export function leaveWarning(): 'performance' | 'timeline' | null {
  if (performanceMode()) return 'performance';
  if (anyTimelineRunning()) return 'timeline';
  return null;
}

/** The next unload is OPButtons' own (an update): do not ask. For ten seconds, so a reload that never happens (an
 *  update that failed) does not switch the guard off for good. */
export function leaveOnPurpose(): void {
  onPurpose = true;
  setTimeout(() => { onPurpose = false; }, 10000);
}

type LeaveWindow = Window & { __opbLeaveGuard?: true };

/** Install once (main or App). */
export function installLeaveGuard(): void {
  const w = window as LeaveWindow;
  if (w.__opbLeaveGuard) return;
  w.__opbLeaveGuard = true;
  window.addEventListener('beforeunload', (e) => {
    if (onPurpose || disabled() || !leaveWarning()) return;
    e.preventDefault();
    // Older Chrome asks only when this is set; the text is the browser's own, whatever it says.
    e.returnValue = '';
  });
}
